///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Toast shown when an alerts sync carries new Extreme/Severe weather
/// alerts. Each entry is a deep link: clicking dispatches the same
/// selectId action as global search, which flies the camera to the alert
/// area, opens its info panel and pushes alert-<id> onto the URL for
/// back/forward + sharing. Severity drives the accent: fuchsia for
/// Extreme, red for Severe, matching the alert polygon color ramp.
///

import { showEntityToast } from "@/components/ui/entity-toast"
import { type AlertSeverity } from "@/earthbrowser/dataset-distribution/alerts/AlertsTable"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { appStore } from "@/redux/store"
import { TriangleAlert } from "lucide-react"

export interface WeatherAlertUpdate {
	/** Alert id (weather_alerts table primary key, used by selectId) */
	id: string
	/** Display label, e.g. "Tornado Warning · Carson, TX" */
	label: string
	severity: AlertSeverity
}

const cSeverityDot: Partial<Record<AlertSeverity, string>> = {
	Extreme: "bg-fuchsia-400",
	Severe: "bg-red-400",
}

export function showWeatherAlertToast(updates: WeatherAlertUpdate[]): void {
	if (updates.length === 0) return

	const hasExtreme = updates.some(u => u.severity === "Extreme")

	showEntityToast({
		title: updates.length === 1 ? "New severe weather alert" : "New severe weather alerts",
		icon: TriangleAlert,
		iconClass: hasExtreme ? "bg-fuchsia-500/15 text-fuchsia-400" : "bg-amber-500/15 text-amber-400",
		variant: hasExtreme ? "extreme" : "severe",
		entries: updates.map(update => ({
			id: update.id,
			label: update.label,
			dotClass: cSeverityDot[update.severity],
			onSelect: () => {
				appStore.dispatch(sendDatasetAction({
					type: "alert",
					action: "selectId",
					id: update.id,
				}))
			},
		})),
	})
}
